import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Warning, CaretDown, CaretRight, ArrowRight, User,
} from "@phosphor-icons/react";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      timeZone: "America/Mexico_City",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return iso; }
}

/**
 * Accordion card that lists all Rejection records linked to a given RCN folio.
 * Hidden entirely when the appointment has no related rejections.
 */
export default function RelatedRejections({ folio }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!folio) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/rejection-records", {
          params: { appointment_folio: folio },
        });
        if (alive) {
          setItems(data || []);
          setOpen((data || []).length > 0);
        }
      } catch (err) {
        console.debug("[RelatedRejections] fetch failed", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [folio]);

  if (loading || items.length === 0) return null;

  const totalPz = items.reduce((s, r) => s + (r.total_pieces || 0), 0);

  return (
    <div className="bg-[#111111] border border-[#FF3B30]/40 rounded-sm mb-6" data-testid="related-rejections">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white/5 transition-colors"
      >
        {open ? <CaretDown size={16} className="text-[#FF3B30]" /> : <CaretRight size={16} className="text-[#FF3B30]" />}
        <Warning size={18} weight="duotone" className="text-[#FF3B30]" />
        <div className="flex-1 text-left">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-[#FF3B30]">
            Rechazos vinculados
          </div>
          <div className="text-sm text-white font-semibold">
            {items.length} rechazo{items.length === 1 ? "" : "s"} · {totalPz} PZ afectada{totalPz === 1 ? "" : "s"}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-white/10">
          <table className="w-full text-sm" data-testid="related-rejections-list">
            <thead className="text-[9px] font-mono-data uppercase tracking-widest text-white/40">
              <tr className="border-b border-white/10 bg-black/30">
                <th className="text-left px-4 py-2">Folio</th>
                <th className="text-left px-4 py-2">Fecha</th>
                <th className="text-left px-4 py-2">Proveedor</th>
                <th className="text-left px-4 py-2">Elaborado</th>
                <th className="text-right px-4 py-2">SKUs</th>
                <th className="text-right px-4 py-2">PZ</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 font-mono-data text-[#FF3B30] font-bold">{r.folio}</td>
                  <td className="px-4 py-2 text-white/70 whitespace-nowrap">{fmt(r.elaboration_date)}</td>
                  <td className="px-4 py-2 text-white/80">{r.provider_name}</td>
                  <td className="px-4 py-2 text-white/60 font-mono-data flex items-center gap-1">
                    <User size={11} /> {r.elaborated_by}
                  </td>
                  <td className="px-4 py-2 text-right font-mono-data text-white">{r.products_count || 0}</td>
                  <td className="px-4 py-2 text-right font-mono-data text-[#F59E0B] font-bold">{r.total_pieces || 0}</td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/rechazos/${r.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#0055FF] hover:text-white"
                    >
                      Abrir <ArrowRight size={10} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
